import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Clock, User, Calendar, ChevronRight, Loader2, CheckCircle, Star } from 'lucide-react';
import mentorService from '@services/mentor.service';
import toast from 'react-hot-toast';

const Questions = () => {
  const navigate = useNavigate();
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'answered'
  const [stats, setStats] = useState({ total: 0, pending: 0, answered: 0, approved: 0 });

  useEffect(() => {
    fetchAllQuestions();
  }, []);

  const fetchAllQuestions = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getAllMentorQuestions();
      
      if (response.success) {
        const questionsData = response?.data?.data?.questions || [];
        const statsData = response?.data?.data?.stats || {};
        setAllQuestions(questionsData);
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
      setAllQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredQuestions = () => {
    if (filter === 'pending') {
      return allQuestions.filter(q => q.status === 'pending');
    } else if (filter === 'answered') {
      return allQuestions.filter(q => q.status === 'answered' || q.status === 'approved');
    }
    return allQuestions;
  };

  const questions = getFilteredQuestions();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleQuestionClick = (questionId) => {
    navigate(`/mentor/questions/${questionId}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header with Filters */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Student Questions</h2>
            <p className="text-sm text-gray-500 mt-1">Manage and respond to student inquiries</p>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total || 0})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-orange-500 text-white'
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              }`}
            >
              Pending ({stats.pending || 0})
            </button>
            <button
              onClick={() => setFilter('answered')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'answered'
                  ? 'bg-green-500 text-white'
                  : 'bg-green-50 text-green-700 hover:bg-green-100'
              }`}
            >
              Answered ({(stats.answered || 0) + (stats.approved || 0)})
            </button>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          </div>
        ) : questions.length > 0 ? (
          <div className="space-y-3">
            {questions.map((question) => (
              <div
                key={question.id}
                onClick={() => handleQuestionClick(question.id)}
                className={`group relative border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${
                  question.status === 'pending'
                    ? 'bg-orange-50 border-orange-200 hover:border-orange-300'
                    : 'bg-green-50 border-green-200 hover:border-green-300'
                }`}
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4">
                  {question.status === 'pending' ? (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-500 text-white rounded-full text-xs font-medium">
                      <Clock className="w-3 h-3" />
                      Pending
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                      <CheckCircle className="w-3 h-3" />
                      Answered
                    </span>
                  )}
                </div>

                {/* Student Info */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-white">
                      {question.student?.firstName?.charAt(0) || 'S'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 pr-20">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {question.student?.firstName && question.student?.lastName
                        ? `${question.student.firstName} ${question.student.lastName}`
                        : 'Student'}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(question.questionDate)}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                </div>

                {/* Question Preview */}
                <p className="text-sm text-gray-700 line-clamp-2 pl-13">
                  {question.question}
                </p>

                {/* Rating Display */}
                {question.status !== 'pending' && question.rating && (
                  <div className="flex items-center gap-2 mt-3 pl-13 pt-3 border-t border-gray-200">
                    <span className="text-xs text-gray-600">Rating:</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-3.5 h-3.5 ${
                            star <= question.rating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-xs font-medium text-gray-700 ml-1">
                        {question.rating}/5
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {filter === 'pending' ? 'No pending questions' : filter === 'answered' ? 'No answered questions' : 'No questions yet'}
            </h3>
            <p className="text-sm text-gray-500">
              {filter === 'pending' 
                ? 'All questions have been answered' 
                : filter === 'answered'
                ? 'Answer some questions to see them here'
                : 'Questions from students will appear here'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Questions;
